import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { useTrainingVideo, useUpdateVideoStatus } from '@/features/roll-review/hooks/useTrainingVideos';
import { useVideoAnnotations, useCreateAnnotation, useDeleteAnnotation } from '@/features/roll-review/hooks/useVideoAnnotations';
import { VideoAnnotation } from '@/types/videoAnnotations';
import { Mic, MicOff, MessageSquare, Play, Pause, Trash2, Volume2, VolumeX, CheckCircle, Clock, Eye, Send } from 'lucide-react';
import { getSignedVideoUrl, getSignedAudioUrl } from '@/services/videoService';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { DeliveryBadge } from '@/features/roll-review/components/DeliveryBadge';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Annotation item
// ---------------------------------------------------------------------------

const AnnotationItem: React.FC<{
  annotation: VideoAnnotation;
  isActive: boolean;
  isLocked: boolean;
  onSeek: (ts: number) => void;
  onDelete: () => void;
}> = ({ annotation, isActive, isLocked, onSeek, onDelete }) => {
  const { t } = useI18n();
  return (
  <div
    className={`rounded-xl overflow-hidden border transition-all ${
      isActive ? 'border-sossiety-red/40' : 'border-white/10'
    }`}
    style={{ backgroundColor: isActive ? 'rgba(215,46,22,0.05)' : '#1A0F0F' }}
  >
    <div className="flex items-start gap-3 p-3 border-l-4 border-sossiety-red">
      <button
        onClick={() => onSeek(annotation.timestamp_seconds)}
        className="flex-shrink-0 text-sossiety-red text-xs font-bold font-mono pt-0.5 hover:opacity-70 transition-opacity"
      >
        {formatTime(annotation.timestamp_seconds)}
      </button>

      <div className="flex-1 min-w-0">
        {annotation.type === 'voice_over' ? (
          <div className="flex items-center gap-2">
            <Volume2 size={13} className="text-sossiety-red flex-shrink-0" />
            <span className="text-white/60 text-xs">{t('video_review.voice_note')}</span>
            {annotation.audio_duration_seconds && (
              <span className="text-white/30 text-xs">
                {formatTime(annotation.audio_duration_seconds)}
              </span>
            )}
          </div>
        ) : (
          <p className="text-white/80 text-sm leading-relaxed">{annotation.content_text}</p>
        )}
      </div>

      {!isLocked && (
        <button
          onClick={onDelete}
          className="flex-shrink-0 text-white/20 hover:text-red-400 transition-colors"
          aria-label="Delete annotation"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  </div>
  );
};

// ---------------------------------------------------------------------------
// Text note modal
// ---------------------------------------------------------------------------

const TextNoteModal: React.FC<{
  timestamp: number;
  onSave: (text: string) => void;
  onClose: () => void;
  isSaving: boolean;
}> = ({ timestamp, onSave, onClose, isSaving }) => {
  const { t } = useI18n();
  const [text, setText] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70">
      <div className="w-full max-w-lg bg-[#1a0f0f] border border-white/10 rounded-t-2xl p-6 pb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold">{t('video_review.text_note_title')}</h3>
            <p className="text-white/40 text-xs mt-0.5">{t('video_review.at_timestamp', { time: formatTime(timestamp) })}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-sm">
            {t('video_review.cancel')}
          </button>
        </div>
        <textarea
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t('video_review.text_note_placeholder')}
          rows={4}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-sossiety-red/60 resize-none"
          maxLength={500}
        />
        <button
          onClick={() => onSave(text)}
          disabled={!text.trim() || isSaving}
          className="mt-3 w-full bg-sossiety-red disabled:opacity-40 text-white font-semibold py-3 rounded-full transition-colors"
        >
          {isSaving ? t('video_review.saving') : t('video_review.save')}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Submit confirmation modal
// ---------------------------------------------------------------------------

type SubmitModalState = 'confirm' | 'warn_no_notes';

const SubmitModal: React.FC<{
  state: SubmitModalState;
  isSubmitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ state, isSubmitting, onConfirm, onClose }) => {
  const { t } = useI18n();

  const title = state === 'warn_no_notes'
    ? t('video_review.submit_no_notes_title')
    : t('video_review.submit_confirm_title');

  const body = state === 'warn_no_notes'
    ? t('video_review.submit_no_notes_body')
    : t('video_review.submit_confirm_body');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70">
      <div className="w-full max-w-lg bg-[#1a0f0f] border border-white/10 rounded-t-2xl p-6 pb-10">
        <h3 className="text-white font-semibold text-base mb-2">{title}</h3>
        <p className="text-white/60 text-sm mb-6 leading-relaxed">{body}</p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-full border border-white/20 text-white/70 text-sm font-medium"
          >
            {t('video_review.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 py-3 rounded-full bg-sossiety-red text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Send size={14} />
            }
            {t('video_review.submit_confirm_action')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Audio review modal
// ---------------------------------------------------------------------------

type PendingAudio = { blob: Blob; durationSecs: number; timestampSeconds: number };

const AudioReviewModal: React.FC<{
  pendingAudio: PendingAudio;
  isSaving: boolean;
  saveError: string;
  onConfirm: () => void;
  onRemove: () => void;
  onReRecord: () => void;
}> = ({ pendingAudio, isSaving, saveError, onConfirm, onRemove, onReRecord }) => {
  const { t } = useI18n();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playError, setPlayError] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(pendingAudio.blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingAudio.blob]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !objectUrl) return;
    if (isPlaying) {
      audio.pause();
    } else {
      setPlayError('');
      audio.play().catch(() => {
        setPlayError(t('video_review.audio_review_play_error'));
        setIsPlaying(false);
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70">
      <div className="w-full max-w-lg bg-[#1a0f0f] border border-white/10 rounded-t-2xl p-6 pb-10">
        <h3 className="text-white font-semibold text-base mb-4">{t('video_review.audio_review_title')}</h3>

        {objectUrl && (
          <audio
            ref={audioRef}
            src={objectUrl}
            onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
          />
        )}

        <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 mb-4">
          <button
            onClick={handlePlayPause}
            className="w-10 h-10 rounded-full bg-sossiety-red flex items-center justify-center flex-shrink-0"
          >
            {isPlaying
              ? <Pause size={18} className="text-white" />
              : <Play size={18} className="ml-0.5 text-white" />
            }
          </button>
          <span className="text-white/60 text-xs font-mono">
            {t('video_review.audio_review_duration', {
              current: formatTime(currentTime),
              total: formatTime(pendingAudio.durationSecs),
            })}
          </span>
        </div>

        {playError && <p className="text-red-400 text-xs mb-3">{playError}</p>}
        {saveError && <p className="text-red-400 text-xs mb-3">{saveError}</p>}

        <div className="flex gap-2 mb-3">
          <button
            onClick={onRemove}
            disabled={isSaving}
            className="flex-1 py-3 rounded-full border border-white/20 text-white/70 text-sm font-medium disabled:opacity-40"
          >
            {t('video_review.audio_review_remove')}
          </button>
          <button
            onClick={onReRecord}
            disabled={isSaving}
            className="flex-1 py-3 rounded-full border border-white/20 text-white/70 text-sm font-medium disabled:opacity-40"
          >
            {t('video_review.audio_review_rerecord')}
          </button>
        </div>

        <button
          onClick={onConfirm}
          disabled={isSaving}
          className="w-full flex items-center justify-center gap-2 bg-sossiety-red disabled:opacity-40 hover:bg-[#b82412] text-white font-semibold py-3 rounded-full transition-colors"
        >
          {isSaving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          {isSaving ? t('video_review.audio_review_saving') : t('video_review.audio_review_confirm')}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const VideoReviewDetail: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const { user } = useAuth();
  const { data: video, isLoading: videoLoading } = useTrainingVideo(videoId);
  const { data: annotations = [], isLoading: annotationsLoading } = useVideoAnnotations(videoId);
  const createAnnotation = useCreateAnnotation(videoId ?? '');
  const deleteAnnotation = useDeleteAnnotation(videoId ?? '');
  const updateStatus = useUpdateVideoStatus();

  // Video player state
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [bufferedPercent, setBufferedPercent] = useState(0);

  // Recording state
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number>(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState('');

  // Pending audio: blob captured but not yet saved (review step)
  const [pendingAudio, setPendingAudio] = useState<PendingAudio | null>(null);
  const [audioSaveError, setAudioSaveError] = useState('');

  // Signed URLs (private bucket)
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  // UI state
  const [showTextModal, setShowTextModal] = useState(false);
  const [submitModalState, setSubmitModalState] = useState<SubmitModalState | null>(null);
  const [pausedAt, setPausedAt] = useState(0);
  const pausedAtRef = useRef(0);
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Sync engine (refs to avoid re-renders and race conditions)
  // ---------------------------------------------------------------------------

  const annotationsRef = useRef<VideoAnnotation[]>([]);
  useEffect(() => { annotationsRef.current = annotations; }, [annotations]);

  const audioUrlMapRef = useRef<Map<string, string>>(new Map());
  const playedInPassRef = useRef<Set<string>>(new Set());
  const isPlayingVoiceRef = useRef(false);

  // Pre-fetch signed audio URLs
  useEffect(() => {
    annotations
      .filter(a => a.type === 'voice_over' && a.audio_url && !audioUrlMapRef.current.has(a.id))
      .forEach(async a => {
        if (!a.audio_url) return;
        const url = await getSignedAudioUrl(a.audio_url);
        if (url) audioUrlMapRef.current.set(a.id, url);
      });
  }, [annotations]);

  // Signed URL for video
  useEffect(() => {
    if (!video?.video_url) return;
    getSignedVideoUrl(video.video_url).then(url => {
      if (url) setVideoSrc(url);
    });
  }, [video?.video_url]);

  // Active annotation highlight (UI only)
  useEffect(() => {
    const hit = annotationsRef.current.find(
      a => Math.abs(a.timestamp_seconds - currentTime) < 1.0
    );
    setActiveAnnotationId(hit?.id ?? null);
  }, [currentTime]);

  // ---------------------------------------------------------------------------
  // handleTimeUpdate: main sync engine (no state deps)
  // ---------------------------------------------------------------------------
  const handleTimeUpdate = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;

    const t = vid.currentTime;
    setCurrentTime(t);

    if (isPlayingVoiceRef.current) return;

    const hit = annotationsRef.current.find(
      a =>
        a.type === 'voice_over' &&
        a.audio_url &&
        t >= a.timestamp_seconds &&
        t < a.timestamp_seconds + 0.5 &&
        !playedInPassRef.current.has(a.id)
    );

    if (!hit || !hit.audio_url) return;

    const signedUrl = audioUrlMapRef.current.get(hit.id);
    if (!signedUrl || !audioRef.current) return;

    playedInPassRef.current.add(hit.id);
    isPlayingVoiceRef.current = true;

    vid.pause();
    audioRef.current.src = signedUrl;
    audioRef.current.play().catch(() => {
      isPlayingVoiceRef.current = false;
      vid.play();
    });
    audioRef.current.onended = () => {
      isPlayingVoiceRef.current = false;
      vid.play().catch(() => null);
      setIsPlaying(true);
    };
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  }, []);

  const handleProgress = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !vid.duration || vid.buffered.length === 0) return;
    setBufferedPercent((vid.buffered.end(vid.buffered.length - 1) / vid.duration) * 100);
  }, []);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (seekTime: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
    playedInPassRef.current.clear();
    isPlayingVoiceRef.current = false;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    handleSeek(ratio * duration);
  };

  const pauseForAnnotation = () => {
    if (videoRef.current && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    const capturedTime = videoRef.current?.currentTime ?? currentTime;
    pausedAtRef.current = capturedTime;
    setPausedAt(capturedTime);
  };

  // Text note
  const handleOpenTextNote = () => {
    pauseForAnnotation();
    setShowTextModal(true);
  };

  const handleSaveTextNote = async (text: string) => {
    if (!videoId) return;
    await createAnnotation.mutateAsync({
      videoId,
      timestampSeconds: pausedAt,
      type: 'note',
      contentText: text,
    });
    setShowTextModal(false);
  };

  // Voice recording
  const handleStartRecording = async () => {
    setRecordingError('');
    pauseForAnnotation();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recordingStartRef.current = Date.now();

      recorder.ondataavailable = e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const durationSecs = (Date.now() - recordingStartRef.current) / 1000;
        setPendingAudio({
          blob,
          durationSecs: Math.round(durationSecs),
          timestampSeconds: pausedAtRef.current,
        });
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch {
      setRecordingError(t('video_review.mic_denied'));
    }
  };

  const handleStopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  const handleDeleteAnnotation = (annotationId: string) => {
    deleteAnnotation.mutate(annotationId);
  };

  // ---------------------------------------------------------------------------
  // Audio review step
  // ---------------------------------------------------------------------------

  const handleConfirmAudio = async () => {
    if (!pendingAudio || !videoId) return;
    setAudioSaveError('');
    try {
      await createAnnotation.mutateAsync({
        videoId,
        timestampSeconds: pendingAudio.timestampSeconds,
        type: 'voice_over',
        audioBlob: pendingAudio.blob,
        audioDurationSeconds: pendingAudio.durationSecs,
      });
      setPendingAudio(null);
    } catch {
      setAudioSaveError(t('video_review.audio_review_save_error'));
    }
  };

  const handleRemoveAudio = () => {
    setPendingAudio(null);
    setAudioSaveError('');
  };

  const handleReRecordAudio = () => {
    setPendingAudio(null);
    setAudioSaveError('');
    handleStartRecording();
  };

  // ---------------------------------------------------------------------------
  // Submit review
  // ---------------------------------------------------------------------------

  const handleSubmitPress = () => {
    if (annotations.length === 0) {
      setSubmitModalState('warn_no_notes');
    } else {
      setSubmitModalState('confirm');
    }
  };

  const handleSubmitConfirm = async () => {
    if (!video) return;
    await updateStatus.mutateAsync({ videoId: video.id, status: 'reviewed' });
    setSubmitModalState(null);
    navigate('/my-roll-reviews');
  };

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  if (videoLoading) {
    return (
      <div className="min-h-screen bg-[#160C0C] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sossiety-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-[#160C0C] flex items-center justify-center">
        <p className="text-white/40">{t('video_review.not_found')}</p>
      </div>
    );
  }

  const isReviewer     = video.reviewer_id === user?.id;
  const isPractitioner = video.uploader_id === user?.id;

  // Guard: only the uploader (practitioner) or the reviewer (instructor) may view this page.
  // RLS protects at the DB level; this provides a clean UI redirect as a second layer.
  if (!isReviewer && !isPractitioner) {
    return <Navigate to="/home" replace />;
  }

  const isDelivered    = video.status === 'reviewed';
  // Annotation tools only available to the reviewer (instructor), never to the practitioner
  const isLocked       = isDelivered || isPractitioner;
  const canSubmit      = isReviewer && !isDelivered && video.status === 'under_review';
  const progress     = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Title: practitioner viewing delivered review → "Feedback from {instructor}"
  const pageTitle = isPractitioner && isDelivered && video.reviewer?.full_name
    ? t('video_review.feedback_from', { name: video.reviewer.full_name })
    : video.title;

  return (
    <>
      <audio ref={audioRef} className="hidden" />

      <SubPageLayout
        title={pageTitle}
        onBack={() => navigate(isPractitioner ? '/my-reviews' : '/my-roll-reviews')}
      >
        <div className="space-y-6">

            {/* Delivery countdown — shown only for reviewer while under_review */}
            {isReviewer && (
              <div className="flex justify-end">
                <DeliveryBadge video={video} />
              </div>
            )}

            {/* Video player with fixed aspect ratio */}
            <div className="aspect-video bg-black rounded-xl overflow-hidden relative">
              <video
                ref={videoRef}
                src={videoSrc ?? undefined}
                className="w-full h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onProgress={handleProgress}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                playsInline
              />
            </div>

            {/* Controls */}
            <div className="space-y-3">
              {/* Progress bar with annotation markers */}
              <div
                className="relative h-8 flex items-center cursor-pointer group"
                onClick={handleProgressClick}
              >
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  {/* buffered but not yet played — muted red */}
                  <div
                    className="absolute h-full rounded-full bg-sossiety-red/30"
                    style={{ width: `${bufferedPercent}%` }}
                  />
                  {/* played — vibrant red */}
                  <div
                    className="absolute h-full rounded-full bg-sossiety-red"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {annotations.map(a => (
                  <button
                    key={a.id}
                    onClick={e => { e.stopPropagation(); handleSeek(a.timestamp_seconds); }}
                    style={{ left: `${duration > 0 ? (a.timestamp_seconds / duration) * 100 : 0}%` }}
                    className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-[#160C0C] transition-transform hover:scale-125 ${
                      a.type === 'voice_over' ? 'bg-sossiety-red' : 'bg-white'
                    }`}
                    title={formatTime(a.timestamp_seconds)}
                  />
                ))}

                {duration > 0 && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-white shadow-md"
                    style={{ left: `${progress}%` }}
                  />
                )}
              </div>

              {/* Time + play/mute */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePlayPause}
                    className="w-10 h-10 rounded-full bg-sossiety-red flex items-center justify-center"
                  >
                    {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                  </button>
                  <span className="text-white/40 text-xs font-mono">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (videoRef.current) {
                      videoRef.current.muted = !isMuted;
                      setIsMuted(!isMuted);
                    }
                  }}
                  className="text-white/40 hover:text-white transition-colors"
                >
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
              </div>

              {/* Annotation actions — hidden when locked */}
              {!isLocked && (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleOpenTextNote}
                    disabled={isRecording}
                    className="flex-1 flex items-center justify-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40 text-white text-sm font-medium py-3 rounded-full transition-colors"
                  >
                    <MessageSquare size={16} />
                    {t('video_review.text_note_btn')}
                  </button>

                  <button
                    onClick={isRecording ? handleStopRecording : handleStartRecording}
                    disabled={createAnnotation.isPending || !!pendingAudio}
                    className={`flex-1 flex items-center justify-center gap-2 text-sm font-medium py-3 rounded-full transition-colors ${
                      isRecording
                        ? 'bg-sossiety-red text-white animate-pulse'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10 text-white'
                    }`}
                  >
                    {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
                    {isRecording ? t('video_review.stop_recording') : t('video_review.record_btn')}
                  </button>
                </div>
              )}

              {recordingError && (
                <p className="text-red-400 text-xs">{recordingError}</p>
              )}

              {createAnnotation.isPending && (
                <p className="text-white/40 text-xs text-center">{t('video_review.saving_annotation')}</p>
              )}
            </div>

            {/* Annotations list */}
            <div>
              <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">
                {t('video_review.annotations_label')} {annotations.length > 0 ? `(${annotations.length})` : ''}
              </p>

              {annotationsLoading ? (
                <div className="space-y-2">
                  {[1, 2].map(i => (
                    <div key={i} className="h-12 bg-white/5 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : annotations.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-8">
                  {isLocked ? t('video_review.no_annotations_delivered') : t('video_review.no_annotations_hint')}
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                  {annotations.map(a => (
                    <AnnotationItem
                      key={a.id}
                      annotation={a}
                      isActive={a.id === activeAnnotationId}
                      isLocked={isLocked}
                      onSeek={handleSeek}
                      onDelete={() => handleDeleteAnnotation(a.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Submit / delivered state */}
            {isDelivered ? (
              <div className="flex items-center justify-center gap-2 bg-green-500/10 border border-green-500/20 rounded-xl py-4">
                <CheckCircle size={18} className="text-green-400" />
                <span className="text-green-400 font-medium text-sm">{t('video_review.review_delivered')}</span>
              </div>
            ) : canSubmit ? (
              <button
                onClick={handleSubmitPress}
                disabled={updateStatus.isPending}
                className="w-full flex items-center justify-center gap-2 bg-sossiety-red disabled:opacity-40 hover:bg-[#b82412] text-white font-semibold py-3 rounded-full transition-colors"
              >
                {updateStatus.isPending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {t('video_review.submit_btn')}
              </button>
            ) : isPractitioner && video.status === 'pending_review' ? (
              /* Practitioner: request sent, waiting for instructor to accept */
              <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3.5">
                <Clock size={18} className="text-amber-400 flex-shrink-0" />
                <div>
                  <p className="text-amber-400 font-medium text-sm">{t('video_review.status_awaiting_acceptance')}</p>
                  <p className="text-white/40 text-xs mt-0.5">{t('video_review.status_awaiting_acceptance_desc')}</p>
                </div>
              </div>
            ) : isPractitioner && video.status === 'under_review' ? (
              /* Practitioner: instructor accepted and is currently reviewing */
              <div className="flex items-center gap-3 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3.5">
                <Eye size={18} className="text-blue-400 flex-shrink-0" />
                <div>
                  <p className="text-blue-400 font-medium text-sm">{t('video_review.status_under_review')}</p>
                  <p className="text-white/40 text-xs mt-0.5">{t('video_review.status_under_review_desc')}</p>
                </div>
              </div>
            ) : null}
        </div>
      </SubPageLayout>

      {showTextModal && (
        <TextNoteModal
          timestamp={pausedAt}
          onSave={handleSaveTextNote}
          onClose={() => setShowTextModal(false)}
          isSaving={createAnnotation.isPending}
        />
      )}

      {submitModalState && (
        <SubmitModal
          state={submitModalState}
          isSubmitting={updateStatus.isPending}
          onConfirm={handleSubmitConfirm}
          onClose={() => setSubmitModalState(null)}
        />
      )}

      {pendingAudio && (
        <AudioReviewModal
          pendingAudio={pendingAudio}
          isSaving={createAnnotation.isPending}
          saveError={audioSaveError}
          onConfirm={handleConfirmAudio}
          onRemove={handleRemoveAudio}
          onReRecord={handleReRecordAudio}
        />
      )}
    </>
  );
};

export default VideoReviewDetail;
